import React, { useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSection from './FAQSection';

const NetSuiteImplementation = () => {
  // Animation controls
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1 });
  const [processRef, processInView] = useInView({ threshold: 0.1 });
  const [benefitsRef, benefitsInView] = useInView({ threshold: 0.1 });
  const [industriesRef, industriesInView] = useInView({ threshold: 0.1 });

  // Helper function to assign colors to industries (add this outside the component)
const getIndustryColor = (index) => {
  const colors = [
    '#4fc3f7', // Light Blue
    '#66bb6a', // Green
    '#ffa726', // Orange
    '#ec407a', // Pink
    '#ab47bc', // Purple
    '#26c6da', // Cyan
    '#ffee58', // Yellow
    '#ff7043', // Deep Orange
    '#8bc34a'  // Light Green
  ];
  return colors[index % colors.length];
};
  useEffect(() => {
    if (inView) controls.start("visible");
  }, [controls, inView]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const processSteps = [
    {
      number: "1",
      title: "Discovery & Planning",
      description: "Analyze your business needs, workflows, and pain points to craft a tailored NetSuite implementation roadmap."
    },
    {
      number: "2",
      title: "System Design & Configuration",
      description: "Tailor and configure NetSuite to meet your business needs, ensuring seamless operations."
    },
    {
      number: "3",
      title: "Data Migration & Integration",
      description: "Securely migrate data from legacy systems and integrate NetSuite, ensuring integrity and continuity."
    },
    {
      number: "4",
      title: "Customization & Automation",
      description: "Enhance NetSuite with custom workflows, dashboards, reports, and automation for optimized performance."
    },
    {
      number: "5",
      title: "User Training & Support",
      description: "Provide hands-on training and 24/7 support to ensure seamless NetSuite adoption and productivity."
    }
  ];

  const benefits = [
    {
      icon: "bi-link-45deg",
      title: "Seamless System Integration",
      description: "Integrate NetSuite with CRM, inventory, and finance systems, creating a single source of truth."
    },
    {
      icon: "bi-puzzle",
      title: "Custom-Fit Implementation",
      description: "Industry-specific NetSuite customization that aligns with your existing processes for faster ROI."
    },
    {
      icon: "bi-headset",
      title: "Expert Support Beyond Setup",
      description: "Ongoing NetSuite consulting and user training to unlock full ERP value."
    },
    {
      icon: "bi-calendar-check",
      title: "On-Time, On-Budget Delivery",
      description: "Clear timelines and milestone-based execution to avoid delays and budget overruns."
    }
  ];

  const industries = [
    {
      name: "Retail & E-commerce",
      icon: "bi-cart",
      description: "Streamline operations, manage inventory, enhance customer experiences, and drive growth.",
      link: "/netsuite/industries/retail-ecommerce"
    },
    {
      name: "Finance & Accounting",
      icon: "bi-cash-stack",
      description: "Automate processes, ensure compliance, and gain real-time financial insights.",
      link: "/netsuite/industries/finance-accounting"
    },
    {
      name: "Manufacturing",
      icon: "bi-gear",
      description: "Optimize manufacturing operations with streamlined production and supply chain management.",
      link: "/netsuite/industries/manufacturing"
    },
    {
      name: "Healthcare & Services",
      icon: "bi-heart-pulse",
      description: "Streamline billing, compliance, and patient management for improved efficiency.",
      link: "/netsuite/industries/healthcare"
    },
    {
      name: "Wholesale & Distribution",
      icon: "bi-truck",
      description: "Enhance operations by streamlining inventory, orders, and supply chain.",
      link: "/netsuite/industries/wholesale-distribution"
    },
    {
      name: "IT & Software",
      icon: "bi-code-slash",
      description: "Automate billing and scale effortlessly with real-time insights.",
      link: "/netsuite/industries/it-software"
    }
  ];

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      color: '#2d3748',
      lineHeight: 1.6,
      overflowX: 'hidden'
    }}>
      <Helmet>
        <title>NetSuite Implementation Experts USA | SpireNest</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/services/implementation" />
        <meta name="description" content="SpireNest specializes in seamless NetSuite ERP implementations for U.S. companies, ensuring timely delivery and minimal business disruption." />
        <meta name="keywords" content="NetSuite implementation, ERP implementation, NetSuite setup, USA, SpireNest" />
        <meta property="og:title" content="NetSuite Implementation Services USA | SpireNest" />
        <meta property="og:description" content="Custom-fit NetSuite ERP implementation that reduces risk, saves time, and drives ROI." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spirenest.co/netsuite-implementation" />
        <meta property="og:image" content="https://spirenest.co/assets/images/og-implementation.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NetSuite Implementation Services USA | SpireNest" />
        <meta name="twitter:description" content="Tailored ERP implementation built around your business goals." />
        <meta name="twitter:image" content="https://spirenest.co/assets/images/og-implementation.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": "https://spirenest.co/services/implementation",
                "name": "SpireNest | NetSuite Implementation Services",
                "description": "Get expert NetSuite implementation services tailored to your business needs. SpireNest ensures a seamless and scalable ERP setup for long-term success.",
                "url": "https://spirenest.co/services/implementation",
                "image": "https://spirenest.co/assets/images/og-home.jpg",
                "publisher": {
                  "@type": "Organization",
                  "name": "SpireNest",
                  "url": "https://spirenest.co/",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://spirenest.co/assets/logo-Cko-LaIl.png"
                  }
                }
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://spirenest.co/services/implementation#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://spirenest.co/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Services",
                    "item": "https://spirenest.co/services/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Implementation",
                    "item": "https://spirenest.co/services/implementation"
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        padding: '100px 0',
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        overflow: 'hidden',
        minHeight:'92vh'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0
        }}>
          <div style={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            background: 'rgba(29, 78, 216, 0.05)',
            borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
            top: '-100px',
            left: '-100px',
            animation: 'float 15s ease-in-out infinite'
          }}></div>
          <div style={{
            position: 'absolute',
            width: '200px',
            height: '200px',
            background: 'rgba(29, 78, 216, 0.03)',
            borderRadius: '63% 37% 54% 46% / 55% 48% 52% 45%',
            bottom: '-50px',
            right: '-50px',
            animation: 'float 12s ease-in-out infinite reverse'
          }}></div>
          <div style={{
            position: 'absolute',
            width: '150px',
            height: '150px',
            background: 'rgba(29, 78, 216, 0.07)',
            borderRadius: '50%',
            top: '30%',
            right: '20%',
            animation: 'float 10s ease-in-out infinite 2s'
          }}></div>
        </div>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} style={{ position: 'relative', zIndex: 2 }}>
              <motion.div
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
                variants={staggerContainer}
                ref={ref}
              >
                <motion.h1 variants={fadeInUp} style={{ fontSize: '3.5rem', fontWeight: 800, marginBottom: '1.5rem',color:'#f97316 ',
                    textShadow: '0 0 10px rgba(249, 115, 22, 0.5' }}>
                  Oracle NetSuite Implementation
                </motion.h1>
                <motion.p variants={fadeInUp} style={{ fontSize: '1.25rem', fontWeight: 400, marginBottom: '2rem',color:'white' }}>
                  At SpireNest, we deliver end-to-end NetSuite implementation services designed to help businesses across industries scale faster and operate smarter.
                </motion.p>
                <motion.div variants={fadeInUp}>
                  <motion.button 
                    className="btn btn-primary btn-lg px-4"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{ 
                      backgroundColor: '#f97316 ', 
                      borderColor: '#1d4ed8',
                      fontSize: '1.1rem',
                      padding: '0.75rem 1.5rem'
                    }}
                  >
                    <a href="/contact" className="btn text-white   px-4">
                    Contact Us
                </a>
                    
                  </motion.button>
                </motion.div>
              </motion.div>
            </Col>
            <Col lg={6} style={{ position: 'relative', zIndex: 2 }}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <div style={{
                  position: 'relative',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)'
                }}>
                  <img 
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80" 
                    alt="NetSuite Implementation" 
                    className="img-fluid"
                    style={{ 
                      width: '100%', 
                      height: 'auto', 
                      display: 'block',
                      borderRadius: '16px'
                    }}
                  />
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Key Features Section */}
      {/* Key Features Section */}
      <section style={{ 
  padding: '6rem 0', 
  background: 'linear-gradient(to bottom, #f8fafc, #ffffff)',
  position: 'relative',
  overflow: 'hidden'
}} ref={featuresRef}>
  {/* Animated background elements */}
  <motion.div 
    className="position-absolute"
    style={{
      top: '10%',
      left: '5%',
      width: '200px',
      height: '200px',
      borderRadius: '50%',
      background: 'rgba(79, 195, 247, 0.1)',
      filter: 'blur(50px)',
      zIndex: 0
    }}
    animate={{
      x: [0, 50, 0],
      y: [0, -30, 0],
      opacity: [0.3, 0.7, 0.3]
    }}
    transition={{
      duration: 15,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  />
  
  <Container style={{ position: 'relative', zIndex: 1 }}>
    <motion.div
      initial="hidden"
      animate={featuresInView ? "visible" : "hidden"}
      variants={staggerContainer}
    >
      {/* Enhanced Header with Gradient Text */}
      <motion.div 
        variants={fadeInUp} 
        style={{ 
          textAlign: 'center', 
          marginBottom: '4rem',
          position: 'relative'
        }}
      >
        <motion.span
          style={{
            display: 'inline-block',
            background: 'linear-gradient(90deg, #1d4ed8, #4fc3f7)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
            fontSize: '0.9rem',
            fontWeight: 600,
            letterSpacing: '1px',
            marginBottom: '1rem',
            textTransform: 'uppercase'
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          NetSuite Implementation
        </motion.span>
        
        <motion.h2 
          style={{ 
            fontWeight: 800, 
            marginBottom: '1rem', 
            color: '#1e293b',
            fontSize: '2.5rem',
            lineHeight: 1.2
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          Delivering Smarter <span style={{ 
            background: 'linear-gradient(90deg, #1d4ed8, #4fc3f7)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent'
          }}>ERP Solutions</span>
        </motion.h2>
        
        <motion.p 
          style={{ 
            fontSize: '1.25rem', 
            color: '#64748b',
            maxWidth: '700px',
            margin: '0 auto'
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          Our certified consultants ensure a seamless, cost-effective rollout with continuous optimization
        </motion.p>
      </motion.div>

      {/* Enhanced Feature Cards with Creative Animations */}
      <Row className="g-4">
        {[
          {
            icon: "bi-building-gear",
            title: "Industry-Specific Expertise",
            description: "Customized configurations for manufacturing, retail, wholesale distribution, SaaS, and professional services.",
            color: '#1d4ed8'
          },
          {
            icon: "bi-people-fill",
            title: "Certified Consultants",
            description: "Experienced U.S.-based consultants guide you from discovery to go-live and beyond.",
            color: '#4fc3f7'
          },
          {
            icon: "bi-speedometer2",
            title: "Streamlined Deployment",
            description: "Proven phased approach ensures smooth transition with minimal business disruption.",
            color: '#10b981'
          },
          {
            icon: "bi-database",
            title: "Data Migration Experts",
            description: "Handle complex ERP data migration and third-party app connections.",
            color: '#f59e0b'
          },
          {
            icon: "bi-graph-up-arrow",
            title: "Post-Go-Live Support",
            description: "Continuous optimization of your NetSuite environment for long-term success.",
            color: '#ec4899'
          },
          {
            icon: "bi-currency-dollar",
            title: "Reduced Operational Costs",
            description: "Automation and streamlined workflows help you save on time, labor, and resources.",
            color: '#8b5cf6'
          }
        ].map((feature, index) => (
          <Col lg={4} md={6} key={index}>
            <motion.div 
              variants={fadeInUp}
              whileHover={{ y: -10 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <motion.div
                style={{
                  height: '100%',
                  background: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                  overflow: 'hidden',
                  position: 'relative',
                  border: '1px solid rgba(0,0,0,0.03)'
                }}
                whileHover={{ 
                  boxShadow: `0 15px 40px ${feature.color}20`
                }}
              >
                {/* Animated accent bar */}
                <motion.div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '4px',
                    background: feature.color
                  }}
                  initial={{ scaleX: 0, originX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                />
                
                <Card.Body style={{ 
                  padding: '2rem',
                  position: 'relative',
                  zIndex: 1
                }}>
                  {/* Animated icon */}
                  <motion.div
                    style={{ 
                      fontSize: '2.5rem',
                      color: feature.color,
                      marginBottom: '1.5rem',
                      display: 'inline-flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: `${feature.color}10`
                    }}
                    whileHover={{
                      rotate: 15,
                      scale: 1.1
                    }}
                  >
                    <i className={`bi ${feature.icon}`}></i>
                  </motion.div>
                  
                  <h5 style={{ 
                    fontWeight: 700, 
                    color: '#1e293b',
                    marginBottom: '1rem',
                    fontSize: '1.25rem'
                  }}>
                    {feature.title}
                  </h5>
                  
                  <p style={{ 
                    color: '#64748b',
                    lineHeight: 1.6
                  }}>
                    {feature.description}
                  </p>
                  
                  {/* Pulse animation element */}
                  <motion.div
                    style={{
                      width: '40px',
                      height: '4px',
                      background: feature.color,
                      borderRadius: '2px',
                      marginTop: '1.5rem'
                    }}
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ 
                      scale: [1, 1.05, 1],
                      opacity: 1
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      repeatType: "reverse",
                      delay: index * 0.1
                    }}
                    viewport={{ once: true }}
                  />
                </Card.Body>
              </motion.div>
            </motion.div>
          </Col>
        ))}
      </Row>
    </motion.div>
  </Container>
</section>

      {/* Process Section */}
      <section style={{ 
        padding: '6rem 0', 
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        position: 'relative',
        overflow: 'hidden'
      }} ref={processRef}>
        {/* Background decorative elements */}
        <div style={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: '300px',
          height: '300px',
          background: 'rgba(29, 78, 216, 0.05)',
          borderRadius: '50%',
          filter: 'blur(100px)'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '10%',
          width: '200px',
          height: '200px',
          background: 'rgba(79, 195, 247, 0.08)',
          borderRadius: '50%',
          filter: 'blur(80px)'
        }}></div>
        
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial="hidden"
            animate={processInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            {/* Enhanced Header */}
            <motion.div variants={fadeInUp} style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <motion.span
                style={{
                  display: 'inline-block',
                  background: 'linear-gradient(90deg, #1d4ed8, #4fc3f7)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  letterSpacing: '1px',
                  marginBottom: '1rem',
                  textTransform: 'uppercase'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Implementation Process
              </motion.span>
              <h2 style={{ 
                fontWeight: 800, 
                marginBottom: '1rem',
                color: '#1e293b',
                fontSize: '2.8rem',
                lineHeight: 1.2
              }}>
                Our NetSuite <span style={{
                  background: 'linear-gradient(90deg, #1d4ed8, #4fc3f7)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent'
                }}>Implementation Process</span>
              </h2>
              <p style={{ 
                fontSize: '1.3rem', 
                color: '#64748b',
                maxWidth: '700px',
                margin: '0 auto',
                lineHeight: 1.6
              }}>
                A structured, proven approach for successful ERP deployment with minimal disruption
              </p>
            </motion.div>

            {/* Enhanced Timeline */}
            <div style={{
              position: 'relative',
              maxWidth: '1200px',
              margin: '0 auto',
              padding: '60px 0'
            }}>
              {/* Animated Timeline Line */}
              <motion.div
                style={{
                  position: 'absolute',
                  width: '4px',
                  background: 'linear-gradient(to bottom, #1d4ed8, #4fc3f7, #10b981)',
                  top: 0,
                  bottom: 0,
                  left: '50%',
                  marginLeft: '-2px',
                  borderRadius: '2px',
                  boxShadow: '0 0 20px rgba(29, 78, 216, 0.3)'
                }}
                initial={{ scaleY: 0, originY: 0 }}
                whileInView={{ scaleY: 1 }}
                transition={{ duration: 2, ease: "easeOut" }}
                viewport={{ once: true }}
              />
              
              {processSteps.map((step, index) => (
                <motion.div 
                  key={index}
                  variants={fadeInUp}
                  className="process-step"
                  style={{
                    padding: '0 60px',
                    position: 'relative',
                    width: '50%',
                    boxSizing: 'border-box',
                    left: index % 2 === 0 ? '0' : '50%',
                    marginBottom: '3rem'
                  }}
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {/* Enhanced Step Number Circle */}
                  <motion.div 
                    className="step-number"
                    style={{
                      position: 'absolute',
                      width: '60px',
                      height: '60px',
                      background: `linear-gradient(135deg, #1d4ed8, #4fc3f7)`,
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.2rem',
                      zIndex: 3,
                      right: index % 2 === 0 ? '-30px' : 'auto',
                      left: index % 2 !== 0 ? '-30px' : 'auto',
                      boxShadow: '0 10px 25px rgba(29, 78, 216, 0.3)',
                      border: '4px solid white'
                    }}
                    initial={{ scale: 0, rotate: -180 }}
                    whileInView={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    whileHover={{ 
                      scale: 1.1,
                      boxShadow: '0 15px 35px rgba(29, 78, 216, 0.4)'
                    }}
                  >
                    {step.number}
                  </motion.div>
                  
                  {/* Enhanced Step Card */}
                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card style={{
                      border: 'none',
                      borderRadius: '20px',
                      boxShadow: '0 15px 35px rgba(0, 0, 0, 0.08)',
                      background: 'linear-gradient(135deg, #ffffff, #f8fafc)',
                      padding: '0',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      {/* Card accent border */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '4px',
                        background: `linear-gradient(90deg, #1d4ed8, #4fc3f7)`
                      }}></div>
                      
                      <Card.Body style={{ padding: '2.5rem' }}>
                        <h5 style={{ 
                          fontWeight: 700,
                          color: '#1e293b',
                          fontSize: '1.4rem',
                          marginBottom: '1rem'
                        }}>
                          {step.title}
                        </h5>
                        <p style={{ 
                          color: '#64748b',
                          fontSize: '1.1rem',
                          lineHeight: 1.7,
                          marginBottom: '1.5rem'
                        }}>
                          {step.description}
                        </p>
                        
                        {/* Progress indicator */}
                        <motion.div
                          style={{
                            width: '60px',
                            height: '3px',
                            background: `linear-gradient(90deg, #1d4ed8, #4fc3f7)`,
                            borderRadius: '2px'
                          }}
                          initial={{ width: 0 }}
                          whileInView={{ width: '60px' }}
                          transition={{ duration: 0.8, delay: 0.5 }}
                          viewport={{ once: true }}
                        />
                      </Card.Body>
                    </Card>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Benefits Section */}
      {/* Benefits Section */}
      <section style={{ padding: '5rem 0', backgroundColor: '#f8fafc' }} ref={benefitsRef}>
        <Container>
          <motion.div
            initial="hidden"
            animate={benefitsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <h2 style={{ fontWeight: 800, marginBottom: '1rem', color: '#1e293b' }}>Benefits of NetSuite Implementation</h2>
              <p style={{ fontSize: '1.25rem', color: '#64748b' }}>Why businesses choose our implementation services</p>
            </motion.div>

            <Row className="g-4">
              {benefits.map((benefit, index) => (
                <Col lg={3} md={6} key={index}>
                  <motion.div variants={fadeInUp} style={{ height: '100%' }}>
                    <Card style={{
                      height: '100%',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                      backgroundColor: 'black',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Card.Body style={{
                        padding: '2rem',
                        flex: '1',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <div style={{ 
                          fontSize: '2rem', 
                          color: '#1d4ed8', 
                          marginBottom: '1rem',
                          textAlign: 'center'
                        }}>
                          <i className={`bi ${benefit.icon}`}></i>
                        </div>
                        <h5 className='text-white' style={{ 
                          fontWeight: 600, 
                          color: '#1e293b',
                          textAlign: 'center',
                          marginBottom: '1rem'
                        }}>
                          {benefit.title}
                        </h5>
                        <p className='text-white' style={{ 
                          color: '#64748b',
                          textAlign: 'center',
                          marginTop: 'auto'
                        }}>
                          {benefit.description}
                        </p>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Industries Section */}
<section style={{ padding: '6rem 0', backgroundColor: '#ffffff' }} ref={industriesRef}>
  <Container>
    <motion.div
      initial="hidden"
      animate={industriesInView ? "visible" : "hidden"}
      variants={staggerContainer}
    >
      <motion.div variants={fadeInUp} style={{ textAlign: 'center', marginBottom: '4rem' }}>
        <h2 style={{ 
          fontWeight: 700, 
          marginBottom: '1.5rem',
          color: '#1a237e',
          fontSize: '2.5rem'
        }}>
          Digitizing <span style={{ color: '#4fc3f7' }}>Businesses</span> Across Industries
        </h2>
        <p style={{ 
          fontSize: '1.25rem', 
          color: '#5c6bc0',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          From startups to large enterprises, we provide scalable ERP solutions that adapt to your industry
        </p>
      </motion.div>

      <Row className="g-4">
        {industries.map((industry, index) => (
          <Col lg={4} md={6} key={index}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={industriesInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ 
                y: -10,
                boxShadow: '0 15px 40px rgba(79, 195, 247, 0.15)'
              }}
              style={{
                height: '100%'
              }}
            >
              <Link to={industry.link} style={{ textDecoration: 'none', color: 'inherit' }}>
                <Card style={{
                  height: '100%',
                  border: 'none',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                  transition: 'all 0.4s ease',
                  backgroundColor: 'white',
                  borderBottom: `4px solid ${getIndustryColor(index)}`,
                  padding: '1rem',
                  cursor: 'pointer'
                }}>
                <Card.Body style={{ textAlign: 'center', padding: '2rem 1.5rem' }}>
                  <div style={{ 
                    fontSize: '3rem', 
                    color: getIndustryColor(index), 
                    marginBottom: '1.5rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <i className={`bi ${industry.icon}`}></i>
                  </div>
                  <h5 style={{ 
                    fontWeight: 600, 
                    marginBottom: '1rem',
                    color: '#1a237e',
                    fontSize: '1.25rem'
                  }}>
                    {industry.name}
                  </h5>
                  <p style={{ 
                    color: '#5c6bc0', 
                    marginBottom: '1.5rem',
                    lineHeight: '1.6'
                  }}>
                    {industry.description}
                  </p>
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: '60px' }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    style={{
                      height: '3px',
                      background: `linear-gradient(90deg, ${getIndustryColor(index)}, ${getIndustryColor(index)}80)`,
                      borderRadius: '2px',
                      margin: '0 auto'
                    }}
                  />
                </Card.Body>
                </Card>
              </Link>
            </motion.div>
          </Col>
        ))}
      </Row>
    </motion.div>
  </Container>
</section>



      {/* CTA Section */}
      <section style={{
        position: 'relative',
        padding: '5rem 0',
        overflow: 'hidden',
        color: 'white'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
          zIndex: -1
        }}>
          <div style={{
            content: '""',
            position: 'absolute',
            top: '-100px',
            left: '-100px',
            width: '300px',
            height: '300px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%'
          }}></div>
          <div style={{
            content: '""',
            position: 'absolute',
            bottom: '-100px',
            right: '-100px',
            width: '400px',
            height: '400px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '50%'
          }}></div>
        </div>
        <Container>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <h2 style={{ fontWeight: 800, marginBottom: '1.5rem' }}>Get Started with SpireNest Today!</h2>
            <p style={{ fontSize: '1.25rem', marginBottom: '2.5rem' }}>
              Unlock the true power of NetSuite with our implementation services for a smooth transition and long-term success.
            </p>
            <motion.button
              className="btn btn-light btn-lg px-4"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                fontSize: '1.1rem',
                padding: '0.75rem 1.5rem',
                backgroundColor: 'white',
                color: '#1d4ed8',
                border: 'none',
                fontWeight: 600
              }}
            >
               <a href="/contact" className="btn  btn-lg px-4">
               Contact Us for a Free Consultation
                </a>
             
            </motion.button>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* Add keyframes for floating animation */}
      <style>{`
        @keyframes float {
          0% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(20px, 20px) rotate(5deg); }
          100% { transform: translate(0, 0) rotate(0deg); }
        }
        
        @media (max-width: 992px) {
          .process-step {
            width: 100% !important;
            padding-left: 70px !important;
            padding-right: 25px !important;
            left: 0 !important;
          }
          
          .step-number {
            left: 20px !important;
            right: auto !important;
          }
          
          .timeline-line {
            left: 40px !important;
          }
          
          .hero-section {
            padding: 80px 0 !important;
          }
        }
        
        @media (max-width: 768px) {
          .hero-section h1 {
            font-size: 2.5rem !important;
          }
          
          .benefit-card, .industry-card {
            margin-bottom: 20px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default NetSuiteImplementation;